import React, { useState } from 'react';
import { Box, TextField, Popover, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';

const ColorButton = styled(IconButton)(({ theme }) => ({
    width: 24,
    height: 24,
    padding: 0,
    border: `1px solid ${theme.palette.divider}`,
    marginRight: theme.spacing(1),
}));

const ColorGrid = styled(Box)(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: 'repeat(8, 1fr)',
    gap: theme.spacing(0.5),
    padding: theme.spacing(1),
    width: 200,
}));

interface ColorPickerProps {
    value: string;
    onChange: (color: string) => void;
    label?: string;
}

const defaultColors = [
    '#000000', '#FFFFFF', '#FF0000', '#00FF00', '#0000FF', '#FFFF00', '#FF00FF', '#00FFFF',
    '#808080', '#C0C0C0', '#800000', '#008000', '#000080', '#808000', '#800080', '#008080',
    '#FFA500', '#FFC0CB', '#800000', '#FF4500', '#DA70D6', '#FA8072', '#32CD32', '#87CEEB',
    '#FFD700', '#F0E68C', '#DDA0DD', '#98FB98', '#F0FFF0', '#F5DEB3', '#DEB887', '#D2691E',
];

export const ColorPicker: React.FC<ColorPickerProps> = ({ value, onChange, label }) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleColorSelect = (color: string) => {
        onChange(color);
        handleClose();
    };

    return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <TextField
                label={label}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                size="small"
                fullWidth
                placeholder="#000000"
                InputProps={{
                    startAdornment: (
                        <ColorButton
                            onClick={handleClick}
                            sx={{ bgcolor: value || '#ffffff' }}
                        />
                    ),
                }}
            />
            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
            >
                <ColorGrid>
                    {defaultColors.map((color) => (
                        <ColorButton
                            key={color}
                            onClick={() => handleColorSelect(color)}
                            sx={{ bgcolor: color }}
                        />
                    ))}
                </ColorGrid>
            </Popover>
        </Box>
    );
};
